import moment from 'moment';

/**
 * Parse a date string into a valid moment instance, rejecting the sentinel
 * strings ('N/A', 'null', 'undefined') that APIs sometimes return in place
 * of a real date.
 * @param dateString - ISO date string
 * @param utc - parse as UTC (callers then convert with .local())
 * @returns valid moment instance or null
 */
const parseDate = (dateString: string, { utc = false }: { utc?: boolean } = {}): moment.Moment | null => {
    if (!dateString || dateString === 'N/A' || dateString === 'null' || dateString === 'undefined') {
        return null;
    }

    const date = utc ? moment.utc(dateString) : moment(dateString);
    return date.isValid() ? date : null;
};

export const useDateHelpers = () => {
    /**
     * Format a date string to '11:12am on November 3, 2024' format
     * @param dateString - ISO date string
     * @returns formatted string or 'N/A' if invalid
     */
    const formatTimeOnDate = (dateString: string): string => {
        const date = parseDate(dateString, { utc: true });
        return date ? date.local().format('h:mma [on] MMMM D, YYYY') : 'N/A';
    };

    /**
     * Format a date string to "June 17th, 2025 2:25pm" format
     * @param dateString - ISO date string
     * @returns formatted date string or "N/A" if invalid
     */
    const formatDate = (dateString: string, format: string = 'MMMM Do, YYYY h:mma'): string => {
        const date = parseDate(dateString, { utc: true });
        return date ? date.local().format(format) : 'N/A';
    };

    /**
     * Format a date string to "5/12/25 4:02pm" format
     * @param dateString - ISO date string
     * @returns formatted date string or "N/A" if invalid
     */
    const formatDateWithoutWord = (dateString: string, format: string = 'M/D/YY h:mma'): string => {
        const date = parseDate(dateString, { utc: true });
        return date ? date.local().format(format) : 'N/A';
    };

    /**
     * Format a date string to date only, without the time (e.g., "5/12/25")
     * @param dateString - ISO date string
     * @param format - Optional format string (default: 'M/D/YY')
     * @returns formatted date string or "N/A" if invalid
     */
    const formatDateOnly = (dateString: string, format: string = 'M/D/YY'): string => {
        const date = parseDate(dateString, { utc: true });
        return date ? date.local().format(format) : 'N/A';
    };


    /**
     * Format a date string to relative time (e.g., "2 hours ago", "3 days ago")
     * @param dateString - ISO date string
     * @returns relative time string
     */
    const formatRelativeTime = (dateString: string): string => {
        const date = parseDate(dateString);
        return date ? date.fromNow() : '';
    };

    /**
     * Format a date string to short date format (e.g., "Jun 17, 2025")
     * @param dateString - ISO date string
     * @returns short date string
     */
    const formatShortDate = (dateString: string): string => {
        const date = parseDate(dateString);
        return date ? date.format('MMM D, YYYY') : '';
    };

    /**
     * Format a date string to time only (e.g., "2:25 PM")
     * @param dateString - ISO date string
     * @returns time string
     */
    const formatTime = (dateString: string): string => {
        const date = parseDate(dateString);
        return date ? date.format('h:mm A') : '';
    };

    /**
     * Check if a date is today
     * @param dateString - ISO date string
     * @returns boolean
     */
    const isToday = (dateString: string): boolean => {
        const date = parseDate(dateString);
        return date ? date.isSame(moment(), 'day') : false;
    };

    /**
     * Check if a date is in the past
     * @param dateString - ISO date string
     * @returns boolean
     */
    const isPast = (dateString: string): boolean => {
        const date = parseDate(dateString);
        return date ? date.isBefore(moment()) : false;
    };

    /**
     * Calculate a person's current age in whole years from their date of birth
     * @param dateOfBirth - date string or Date object
     * @returns age in years or null when no date of birth is provided
     */
    const calculateAge = (dateOfBirth: string | Date): number | null => {
        if (!dateOfBirth) return null;

        const birthDate = new Date(dateOfBirth);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }

        return age;
    };

    /**
     * Intelligently format dates based on available precision without adding phantom values
     * Handles multiple input formats:
     * - Year only (e.g., "1986") → displays "1986"
     * - Month + Year (e.g., "June 1986", "06/1986", "1986-06") → displays "June 1986"
     * - Month + Day (e.g., "06-28", "06-28T00:00:00Z") → displays "June 28"
     * - Full Date (e.g., "06/21/1986", "1986-06-21") → displays full date as "June 21, 1986"
     *
     * @param dateInput - Date input in various formats (string or number)
     * @param fullDateFormat - Format for full dates (default: 'MMMM D, YYYY')
     * @param monthYearFormat - Format for month+year (default: 'MMMM YYYY')
     * @param yearFormat - Format for year only (default: 'YYYY')
     * @returns Formatted date string based on available precision or 'N/A' if invalid
     */
    const formatDateWithPrecision = (
        dateInput: string | number,
        fullDateFormat: string = 'MMMM D, YYYY',
        monthYearFormat: string = 'MMMM YYYY',
        yearFormat: string = 'YYYY'
    ): string => {
        // Handle null/undefined/empty values
        if (!dateInput || dateInput === 'N/A' || dateInput === 'null' || dateInput === 'undefined') {
            return 'N/A';
        }

        const input = String(dateInput).trim();

        // Handle year-only format (4 digits)
        if (/^\d{4}$/.test(input)) {
            const year = parseInt(input, 10);
            if (year >= 1000 && year <= 9999) {
                return moment().year(year).format(yearFormat);
            }
            return 'N/A';
        }

        // Handle month names with year (e.g., "June 1986", "January 2024")
        const monthYearPattern = /^([A-Za-z]+)\s+(\d{4})$/;
        const monthYearMatch = input.match(monthYearPattern);
        if (monthYearMatch) {
            const [, monthName, yearStr] = monthYearMatch;
            const testDate = moment(`${monthName} 1, ${yearStr}`, 'MMMM D, YYYY');
            if (testDate.isValid()) {
                return testDate.format(monthYearFormat);
            }
            return 'N/A';
        }

        // Handle MM/YYYY format (e.g., "06/1986", "12/2024")
        const mmYyyyPattern = /^(\d{1,2})\/(\d{4})$/;
        const mmYyyyMatch = input.match(mmYyyyPattern);
        if (mmYyyyMatch) {
            const [, month, year] = mmYyyyMatch;
            const testDate = moment(`${year}-${month.padStart(2, '0')}-01`);
            if (testDate.isValid()) {
                return testDate.format(monthYearFormat);
            }
            return 'N/A';
        }

        // Handle YYYY-MM format (e.g., "1986-06", "2024-12")
        const yyyyMmPattern = /^(\d{4})-(\d{1,2})$/;
        const yyyyMmMatch = input.match(yyyyMmPattern);
        if (yyyyMmMatch) {
            const [, year, month] = yyyyMmMatch;
            const testDate = moment(`${year}-${month.padStart(2, '0')}-01`);
            if (testDate.isValid()) {
                return testDate.format(monthYearFormat);
            }
            return 'N/A';
        }

        // Handle MM-DD format with or without time (e.g., "06-28", "06-28T00:00:00Z")
        // This represents month-day without year - display as "June 28" (no year assumed)
        // Make sure it's NOT a full date format like YYYY-MM-DD
        const mmDdPattern = /^(\d{1,2})-(\d{1,2})(?:T.*)?$/;
        const mmDdMatch = input.match(mmDdPattern);
        if (mmDdMatch && !input.match(/^\d{4}-/)) { // Exclude YYYY-MM-DD formats
            const [, month, day] = mmDdMatch;
            // Create a test date with current year just for validation
            const testDate = moment(`2024-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
            if (testDate.isValid()) {
                // Format without year to show only month and day
                return testDate.format('MMMM D');
            }
            return 'N/A';
        }

        // Try to parse as a full date using moment's flexible parsing
        // This handles formats like "06/21/1986", "1971-06-28T00:00:00Z", "June 21, 1986", etc.
        const fullDate = moment(input);
        if (fullDate.isValid()) {
            // Check if the input looks like it has day precision
            // Common full date patterns
            const fullDatePatterns = [
                /^\d{1,2}\/\d{1,2}\/\d{2,4}$/,       // MM/DD/YYYY or M/D/YY
                /^\d{4}-\d{1,2}-\d{1,2}.*$/,         // YYYY-MM-DD with optional time
                /^[A-Za-z]+\s+\d{1,2},?\s+\d{4}$/,   // Month DD, YYYY
                /^\d{1,2}\s+[A-Za-z]+\s+\d{4}$/      // DD Month YYYY
            ];

            const hasFullDatePattern = fullDatePatterns.some(pattern => pattern.test(input));

            if (hasFullDatePattern) {
                return fullDate.format(fullDateFormat);
            }
        }

        // If nothing matches, return N/A
        return 'N/A';
    };

    return {
        formatDate,
        formatRelativeTime,
        formatShortDate,
        formatTime,
        isToday,
        isPast,
        formatTimeOnDate,
        formatDateWithoutWord,
        formatDateOnly,
        formatDateWithPrecision,
        calculateAge
    };
};
